import * as React from "react";
import { cn } from "../lib/cn.js";

/**
 * Tiny zero-dependency SVG sparkline. Used by StatCard to convey trend
 * without pulling recharts into the critical-path bundle. Auto-scales
 * the data into the viewBox; `trend` controls the stroke colour.
 */

export interface SparklineProps extends React.SVGAttributes<SVGSVGElement> {
  data: number[];
  /** Visual width:height ratio. */
  aspectRatio?: number;
  trend?: "up" | "down" | "flat";
  strokeWidth?: number;
}

export function Sparkline({
  data,
  aspectRatio = 4,
  trend,
  strokeWidth = 1.75,
  className,
  ...props
}: SparklineProps) {
  if (data.length < 2) return null;

  const width = 100;
  const height = width / aspectRatio;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  const inferredTrend: NonNullable<SparklineProps["trend"]> =
    trend ??
    (data[data.length - 1]! > data[0]!
      ? "up"
      : data[data.length - 1]! < data[0]!
        ? "down"
        : "flat");

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn(
        "h-8 w-24",
        inferredTrend === "up" && "text-emerald-500",
        inferredTrend === "down" && "text-rose-500",
        inferredTrend === "flat" && "text-muted-foreground",
        className,
      )}
      aria-hidden="true"
      {...props}
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
